import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { QueryCondition, Operator, getFieldDefinition, getOperatorsForFieldType } from "@/types/queryBuilder";
import { FieldSelector } from "./FieldSelector";
import { OperatorSelector } from "./OperatorSelector";
import { ValueInput } from "./ValueInput";

interface ConditionRowProps {
	condition: QueryCondition;
	onUpdate: (updates: Partial<QueryCondition>) => void;
	onRemove: () => void;
}

export function ConditionRow({ condition, onUpdate, onRemove }: ConditionRowProps) {
	const { t } = useTranslation();

	const handleFieldChange = (field: string) => {
		// Reset operator and value when field changes
		const fieldDef = getFieldDefinition(field);
		const operators = getOperatorsForFieldType(fieldDef?.type || 'text');
		const defaultOperator = operators[0]?.key || 'equals';

		onUpdate({
			field,
			operator: defaultOperator as Operator,
			value: fieldDef?.type === 'array' ? [] : '',
			secondValue: null
		});
	};

	const handleOperatorChange = (operator: Operator) => {
		// Reset value when operator changes to/from array operators
		const fieldDef = getFieldDefinition(condition.field);
		const needsArray = ['in', 'not_in', 'contains_any', 'contains_all'].includes(operator);
		const hadArray = ['in', 'not_in', 'contains_any', 'contains_all'].includes(condition.operator);

		if (fieldDef?.type === 'language')
			onUpdate({ operator, secondValue: operator === 'any_level' ? null : condition.secondValue });
		else if (needsArray !== hadArray)
			onUpdate({ operator, value: needsArray ? [] : '' });
		else
			onUpdate({ operator });
	};

	const handleValueChange = (value: any, secondValue?: any) => {
		if (secondValue !== undefined)
			onUpdate({ value, secondValue });
		else
			onUpdate({ value });
	};

	return (
		<div className="flex items-center gap-2 p-3 bg-background rounded-sm border shadow-sm hover:shadow-md transition-shadow">
			<FieldSelector
				value={condition.field}
				onChange={handleFieldChange}
			/>
			<OperatorSelector
				fieldKey={condition.field}
				value={condition.operator}
				onChange={handleOperatorChange}
			/>
			<div className="flex-1">
				<ValueInput
					fieldKey={condition.field}
					operator={condition.operator}
					value={condition.value}
					secondValue={condition.secondValue}
					onChange={handleValueChange}
				/>
			</div>
			<Button
				variant="ghost"
				size="icon"
				className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
				onClick={onRemove}
			>
				<X className="h-4 w-4" />
			</Button>
		</div>
	);
}
